
import React, { useEffect, useState } from 'react';
import { Poll } from '@/types/poll';
import PollDisplay from './poll-display/PollDisplay';
import { useAuth } from '@/contexts/auth';
import { useAnonymousVoting } from './poll-display/useAnonymousVoting';
import { useCentralizedVoting } from '@/hooks/voting/useCentralizedVoting';

interface UniversalPollDisplayProps {
  polls: Poll[];
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>;
  userVotes: Record<string, number>;
  setUserVotes: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  currentPoll: Poll;
  markRecentVote?: (pollId: string) => void;
  onRefresh?: () => void;
  variant?: 'default' | 'compact' | 'embedded';
  showShareButtons?: boolean;
  showLocation?: boolean;
  showTrendingBadge?: boolean;
  cannotVoteReason?: string;
}

const UniversalPollDisplay: React.FC<UniversalPollDisplayProps> = ({
  polls,
  setPolls,
  userVotes,
  setUserVotes,
  currentPoll,
  markRecentVote,
  onRefresh,
  variant = 'default',
  showShareButtons = true,
  showLocation = true,
  showTrendingBadge = true,
  cannotVoteReason
}) => {
  const { isAuthenticated } = useAuth();
  
  // Get anonymous voting context for current poll
  const {
    hasAnonymousVoted
  } = useAnonymousVoting(currentPoll?.id, isAuthenticated);
  
  // Use centralized voting with enhanced selected option tracking
  const { 
    submitVote: centralizedSubmitVote, 
    validateVote, 
    getUserSelectedOption,
    isProcessing 
  } = useCentralizedVoting();

  // Track selected option for current poll
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  // Load selected option when poll changes
  useEffect(() => {
    const loadSelectedOption = async () => {
      if (currentPoll?.id) {
        try {
          const selected = await getUserSelectedOption(currentPoll.id);
          setSelectedOption(selected);
        } catch (error) {
          console.error('Error loading selected option:', error);
          setSelectedOption(null);
        }
      }
    };

    loadSelectedOption();
  }, [currentPoll?.id, getUserSelectedOption]);

  // Enhanced vote handler that uses centralized voting
  const onVote = async (optionId: number) => {
    try {
      markRecentVote?.(currentPoll.id);
      
      const result = await centralizedSubmitVote(currentPoll, optionId);
      
      if (result.success) {
        // Update local selected option state
        setSelectedOption(optionId);
        
        // Update user votes state
        setUserVotes(prev => ({
          ...prev,
          [currentPoll.id]: optionId
        }));

        // Optimistically update poll counts
        setPolls(currentPolls => currentPolls.map(p => {
          if (p.id === currentPoll.id) {
            const updatedOptions = p.options.map(option => {
              if (option.id === optionId) {
                return {
                  ...option,
                  votes: option.votes + 1
                };
              }
              return option;
            });

            const newTotalVotes = p.total_votes + 1;
            const optionsWithPercentages = updatedOptions.map(option => ({
              ...option,
              percentage: newTotalVotes > 0 ? Math.round((option.votes / newTotalVotes) * 100) : 0
            }));

            return {
              ...p,
              options: optionsWithPercentages,
              total_votes: newTotalVotes,
              questions: {
                ...p.questions,
                options: optionsWithPercentages,
                total_votes: newTotalVotes
              }
            };
          }
          return p;
        }));

        // Refresh after a delay to get accurate counts
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error in UniversalPollDisplay vote handler:', error);
    }
  };

  // Enhanced can vote logic
  const canVote = async (): Promise<boolean> => {
    try {
      return await validateVote(currentPoll.id);
    } catch (error) {
      console.error('Error validating vote:', error);
      return false;
    }
  };

  // Check if user has voted (including anonymous and centralized tracking)
  const hasVoted = userVotes[currentPoll.id] !== undefined || 
                   hasAnonymousVoted || 
                   selectedOption !== null;

  // Create enhanced poll display with selected option
  const enhancedPoll = { 
    ...currentPoll,
    results: {
      ...(currentPoll.results || { totalVotes: currentPoll.total_votes, topOption: '' }),
      selectedOptionId: selectedOption || userVotes[currentPoll.id]
    }
  };

  return (
    <PollDisplay
      poll={enhancedPoll}
      hasVoted={hasVoted}
      canVote={!hasVoted && !isProcessing}
      cannotVoteReason={cannotVoteReason}
      onVote={onVote}
      onRefresh={onRefresh}
      variant={variant}
      showShareButtons={showShareButtons}
      showLocation={showLocation}
      showTrendingBadge={showTrendingBadge}
    />
  );
};

export default UniversalPollDisplay;
